import { useMutation, useQuery, useQueryClient, QueryClient } from "react-query";
import api from "../api";
import { API_ROUTES } from "../../utils/const";
import {
  ApiNotification,
  EmailPreferenceFlags,
  EmailPreferences,
  NotificationsPageResponse,
} from "../../components/NotificationsCentre/types";
import { queryClient as appQueryClient } from "../queryClient";

export const NOTIFICATIONS_QUERY_KEY = "notifications";
export const NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY = "notificationsUnreadCount";
export const EMAIL_PREFERENCES_QUERY_KEY = "emailPreferences";

export const invalidateNotificationsQueries = (
  client: QueryClient = appQueryClient
) => {
  client.invalidateQueries([NOTIFICATIONS_QUERY_KEY]);
  client.invalidateQueries([NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY]);
};

type NotificationsQueryParams = {
  page?: number;
  size?: number;
  onlyUnread?: boolean;
  enabled?: boolean;
};

const FETCH_NOTIFICATIONS = async ({
  page = 0,
  size = 20,
  onlyUnread = false,
}: Omit<NotificationsQueryParams, "enabled">) => {
  const response = await api.get<NotificationsPageResponse>(
    API_ROUTES.GET_NOTIFICATIONS,
    {
      params: {
        page,
        size,
        onlyUnread,
      },
    }
  );

  return response.data;
};

const FETCH_UNREAD_COUNT = async () => {
  const response = await api.get<number>(
    API_ROUTES.GET_NOTIFICATIONS_UNREAD_COUNT
  );

  return response.data;
};

const MARK_NOTIFICATION_READ = async (id: number) => {
  const response = await api.patch(
    `${API_ROUTES.GET_NOTIFICATIONS}/${id}/read`
  );

  return response.data;
};

const MARK_ALL_NOTIFICATIONS_READ = async () => {
  const response = await api.patch<number>(
    API_ROUTES.MARK_ALL_NOTIFICATIONS_READ
  );

  return response.data;
};

const emailPreferencesUrl = (businessId: number | null) => {
  if (!businessId) {
    throw new Error("Business ID is required to manage email preferences.");
  }

  return `${API_ROUTES.MERCHANT_BASIC_SETTINGS}/${businessId}/email-preferences`;
};

const FETCH_EMAIL_PREFERENCES = async (businessId: number | null) => {
  const response = await api.get<EmailPreferences>(
    emailPreferencesUrl(businessId)
  );

  return response.data;
};

const UPDATE_EMAIL_PREFERENCES = async ({
  businessId,
  preferences,
}: {
  businessId: number | null;
  preferences: Partial<EmailPreferenceFlags>;
}) => {
  const response = await api.patch<EmailPreferences>(
    emailPreferencesUrl(businessId),
    preferences
  );

  return response.data;
};

export const useNotifications = ({
  page = 0,
  size = 20,
  onlyUnread = false,
  enabled = true,
}: NotificationsQueryParams = {}) => {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, page, size, onlyUnread],
    queryFn: () => FETCH_NOTIFICATIONS({ page, size, onlyUnread }),
    enabled,
    keepPreviousData: true,
    staleTime: 0,
  });
};

export const useUnreadNotificationsCount = (enabled = true) => {
  return useQuery({
    queryKey: [NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY],
    queryFn: FETCH_UNREAD_COUNT,
    enabled,
    refetchInterval: 60000,
    staleTime: 0,
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: MARK_NOTIFICATION_READ,
    onSuccess: () => {
      invalidateNotificationsQueries(queryClient);
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: MARK_ALL_NOTIFICATIONS_READ,
    onSuccess: () => {
      invalidateNotificationsQueries(queryClient);
    },
  });
};

export const useEmailPreferences = (
  businessId: number | null,
  enabled = true
) => {
  return useQuery({
    queryKey: [EMAIL_PREFERENCES_QUERY_KEY, businessId],
    queryFn: () => FETCH_EMAIL_PREFERENCES(businessId),
    enabled: enabled && !!businessId,
  });
};

export const useUpdateEmailPreferences = (businessId: number | null) => {
  const queryClient = useQueryClient();
  const queryKey = [EMAIL_PREFERENCES_QUERY_KEY, businessId];

  return useMutation({
    mutationFn: (preferences: Partial<EmailPreferenceFlags>) =>
      UPDATE_EMAIL_PREFERENCES({ businessId, preferences }),
    onMutate: async (preferences) => {
      await queryClient.cancelQueries(queryKey);
      const previous = queryClient.getQueryData<EmailPreferences>(queryKey);
      if (previous) {
        queryClient.setQueryData(queryKey, { ...previous, ...preferences });
      }

      return { previous };
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, updated);
    },
    onError: (_error, _preferences, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
    },
  });
};

export type { ApiNotification, NotificationsPageResponse };
